import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { v4 as uuid } from "uuid";
import {
  retailPosApi,
  retailPosCache,
  isOfflineError,
  isRecoverableSyncError,
  isSubscriptionDeniedError,
} from "../services/retailPosApi";
import { isDefinitelyOffline, markKnownOffline } from "../../../store/api";
import { retailPosOfflineQueue } from "../services/retailPosOfflineQueue";
import { connectRetailPosSocket } from "../services/retailPosSocket";
import { isSameCatalogVisitor } from "../../../utils/catalogVisitor";
import {
  normPhoneDigits,
  normalizeFullPhone,
  isValidFullPhone,
} from "../../../utils/phoneCountryCodes";
import { normalizeProductoImagenes } from "../utils/productImages";
import { resolveFechaRegistro } from "../utils/fechaRegistro";

const CURRENT_SCHEMA_VERSION = 2;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const toLocalISOString = (date = new Date()) => {
  const d = new Date(date);
  const tzOffsetMs = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, -1);
};

const getLocalDateKey = (dateLike) => {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const nowISO = () => toLocalISOString(new Date());

const cleanText = (value) => String(value || "").trim();
const normalizeKey = (value) => cleanText(value).toUpperCase();
const normalizeCampaignCode = (value) =>
  cleanText(value)
    .toUpperCase()
    .replace(/\s+/g, "");
const normalizeEmail = (value) => cleanText(value).toLowerCase();

const createSku = ({ categoria, nombre, temporada, color, talla, corte }) => {
  const clean = (v, size = 3) =>
    cleanText(v)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, size) || "NA";

  return [
    clean(categoria || "GEN", 3),
    clean(nombre, 4),
    clean(temporada || "BASE", 4),
    clean(color, 3),
    clean(talla, 3),
    clean(corte || "REG", 3),
  ].join("-");
};

const defaultCustomerStats = () => ({
  compras: 0,
  montoAcumulado: 0,
  ticketPromedio: 0,
  frecuenciaDias: null,
  ultimaCompra: null,
  categoriasTop: [],
  segmento: "NUEVO",
  riesgoChurn: "BAJO",
});

const MS_DAY = 1000 * 60 * 60 * 24;
const POLICY_WINDOWS = {
  CAMBIO_TALLA: 15,
  CAMBIO_PRODUCTO: 15,
  DEVOLUCION_CREDITO: 7,
};

const evaluateChangePolicy = ({ ventaFecha, tipo, estadoPrenda }) => {
  const diasLimite = POLICY_WINDOWS[tipo] || 15;
  const diasTranscurridos = Math.max(
    0,
    Math.floor((Date.now() - new Date(ventaFecha).getTime()) / MS_DAY)
  );

  if (diasTranscurridos > diasLimite) {
    return {
      ok: false,
      reason: `Fuera de política: ${diasTranscurridos} días (límite ${diasLimite})`,
      diasLimite,
      diasTranscurridos,
    };
  }

  const condicion = cleanText(estadoPrenda || "SIN_USO").toUpperCase();
  if (!["SIN_USO", "NUEVO", "IMPECABLE"].includes(condicion)) {
    return {
      ok: false,
      reason: "Estado de prenda no elegible para cambio/devolución",
      diasLimite,
      diasTranscurridos,
    };
  }

  return { ok: true, diasLimite, diasTranscurridos };
};

const calculateSubtotal = (items = []) =>
  items.reduce(
    (acc, i) => acc + (parseInt(i.cantidad) || 0) * (parseFloat(i.precio_unitario) || 0),
    0
  );

const calculateCostTotal = (items = []) =>
  items.reduce(
    (acc, i) => acc + (parseInt(i.cantidad) || 0) * (parseFloat(i.costo_unitario) || 0),
    0
  );

const remapProductIdsInPayload = (action, payload, idMap = {}) => {
  const mapId = (value) => {
    const key = cleanText(value);
    return idMap[key] || value;
  };

  if (!payload || typeof payload !== "object") return payload;

  switch (action) {
    case "agregarProducto":
      return {
        ...payload,
        id: mapId(payload.id),
      };

    case "editarProducto":
    case "desactivarProducto":
      return {
        ...payload,
        id: mapId(payload.id),
      };

    case "registrarCompra":
      return {
        ...payload,
        items: Array.isArray(payload.items)
          ? payload.items.map((item) => ({
              ...item,
              producto_id: mapId(item.producto_id),
            }))
          : [],
      };

    case "registrarVenta":
      return {
        ...payload,
        items: Array.isArray(payload.items)
          ? payload.items.map((item) => ({
              ...item,
              producto_id: mapId(item.producto_id),
            }))
          : [],
      };

    case "registrarCambioDevolucion":
      return {
        ...payload,
        producto_origen_id: mapId(payload.producto_origen_id),
        producto_destino_id: mapId(payload.producto_destino_id),
      };

    default:
      return payload;
  }
};

const findExistingProductIdFromPayload = (productos = [], payload = {}) => {
  const barcode = cleanText(payload?.codigo_barras);
  if (barcode) {
    const byBarcode = (productos || []).find(
      (p) => cleanText(p.codigo_barras) && cleanText(p.codigo_barras) === barcode
    );
    if (byBarcode?.id) return byBarcode.id;
  }

  const sku = cleanText(payload?.sku);
  if (sku) {
    const bySku = (productos || []).find(
      (p) => cleanText(p.sku) && cleanText(p.sku) === sku
    );
    if (bySku?.id) return bySku.id;
  }

  return null;
};

const buildAverageCostMap = (productos = [], detalleCompras = []) => {
  const agg = {};

  (detalleCompras || []).forEach((d) => {
    const productoId = cleanText(d.producto_id);
    if (!productoId) return;

    const qty = Math.max(0, parseInt(d.cantidad) || 0);
    const cost = Math.max(0, parseFloat(d.precio_compra) || 0);
    if (qty <= 0 || cost < 0) return;

    if (!agg[productoId]) {
      agg[productoId] = { qty: 0, totalCost: 0 };
    }
    agg[productoId].qty += qty;
    agg[productoId].totalCost += qty * cost;
  });

  const out = {};
  (productos || []).forEach((p) => {
    const productId = cleanText(p.id);
    if (!productId) return;

    const accumulated = agg[productId];
    if (accumulated && accumulated.qty > 0) {
      out[productId] = accumulated.totalCost / accumulated.qty;
      return;
    }

    out[productId] = Math.max(0, parseFloat(p.precio_compra) || 0);
  });

  return out;
};

const canSendWhatsapp = ({ cliente, interacciones, fechaRef }) => {
  if (!cliente) return { ok: false, reason: "Cliente no encontrado" };
  if (!cliente.consentimiento_whatsapp) {
    return { ok: false, reason: "Cliente sin consentimiento WhatsApp" };
  }

  const refDate = new Date(fechaRef);
  const last48h = interacciones.filter(
    (i) => refDate - new Date(i.fecha) <= 2 * MS_DAY
  );
  if (last48h.length > 0) {
    return { ok: false, reason: "Cadencia activa: esperar 48h entre mensajes" };
  }

  const last7d = interacciones.filter(
    (i) => refDate - new Date(i.fecha) <= 7 * MS_DAY
  );
  if (last7d.length >= 2) {
    return { ok: false, reason: "Límite de 2 campañas en 7 días" };
  }

  return { ok: true };
};

const normalizeWhatsappPhone = (phone) => normalizeFullPhone(phone);

const buildWhatsappTemplateText = ({ cliente, tipo, plantilla, metadata = {} }) => {
  const nombre = cleanText(cliente?.nombre || "cliente");
  const estilo = cleanText(metadata?.estilo || cliente?.estilo_preferido || "");
  const categorias = Array.isArray(metadata?.categorias)
    ? metadata.categorias.filter(Boolean).slice(0, 2).join(", ")
    : "";

  const plantillaKey = cleanText(plantilla || tipo).toUpperCase();

  if (plantillaKey === "POST_COMPRA_RECOMENDACION") {
    return `Hola ${nombre}, gracias por tu compra en nuestra tienda. Si quieres, te comparto recomendaciones que combinan con tu estilo ${estilo || "favorito"}.`;
  }

  if (plantillaKey === "NUEVA_COLECCION_AFINIDAD") {
    return `Hola ${nombre}, ya llego nueva coleccion${estilo ? ` de estilo ${estilo.toLowerCase()}` : ""}. ${categorias ? `Tenemos novedades en ${categorias}. ` : ""}Te gustaria que te comparta opciones?`;
  }

  if (plantillaKey === "REPOSICION_TALLA_FAVORITA") {
    return `Hola ${nombre}, tenemos reposicion de tallas y colores que te pueden interesar${categorias ? ` en ${categorias}` : ""}. Te aparto una opcion?`;
  }

  if (plantillaKey === "REACTIVACION_NO_SPAM") {
    return `Hola ${nombre}, te escribimos porque tenemos novedades seleccionadas para ti${estilo ? ` en estilo ${estilo.toLowerCase()}` : ""}. Si quieres, te envio solo 2 recomendaciones.`;
  }

  return `Hola ${nombre}, tenemos novedades en tienda que te pueden interesar. Te comparto opciones?`;
};

const buildWaMeLink = (phone, text) => {
  const normalizedPhone = normalizeWhatsappPhone(phone);
  if (!normalizedPhone) return null;
  return `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(text)}`;
};

const normalizeState = (rawState) => {
  const base = {
    ...estadoInicial,
    ...(rawState || {}),
  };

  const productos = Array.isArray(base.productos)
    ? base.productos.map((p) => {
        const talla = cleanText(p.talla);
        const color = cleanText(p.color);
        const categoria = cleanText(p.categoria || "GENERAL");
        const temporada = cleanText(p.temporada || "BASE");
        const corte = cleanText(p.corte || "REGULAR");
        const imgs = normalizeProductoImagenes(p);
        const firstImg = imgs[0];
        return {
          id: p.id || uuid(),
          nombre: cleanText(p.nombre),
          marca: cleanText(p.marca),
          codigo_barras: cleanText(p.codigo_barras),
          sku: cleanText(p.sku) ||
            createSku({
              categoria,
              nombre: p.nombre,
              temporada,
              color,
              talla,
              corte,
            }),
          producto_base: cleanText(p.producto_base || p.nombre),
          categoria,
          temporada,
          talla,
          color,
          corte,
          imagenes: imgs,
          imagenKey: firstImg?.key || cleanText(p.imagenKey || ""),
          imagenUrl: firstImg?.url || cleanText(p.imagenUrl || ""),
          mostrarEnCatalogo: p.mostrarEnCatalogo !== false,
          precio_compra: parseFloat(p.precio_compra) || 0,
          precio_venta: parseFloat(p.precio_venta) || 0,
          precio_minimo: parseFloat(p.precio_minimo) || 0,
          stock: parseInt(p.stock) || 0,
          stock_reservado: parseInt(p.stock_reservado) || 0,
          activo: p.activo !== false,
          fecha_creacion: p.fecha_creacion || nowISO(),
        };
      })
    : [];

  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    config: {
      ...estadoInicial.config,
      ...(base.config || {}),
      catalogoMostrarCodigoBarras: base.config?.catalogoMostrarCodigoBarras === true,
    },
    productos,
    ventas: Array.isArray(base.ventas) ? base.ventas : [],
    detalleVentas: Array.isArray(base.detalleVentas) ? base.detalleVentas : [],
    compras: Array.isArray(base.compras) ? base.compras : [],
    detalleCompras: Array.isArray(base.detalleCompras) ? base.detalleCompras : [],
    movimientosInventario: Array.isArray(base.movimientosInventario)
      ? base.movimientosInventario
      : [],
    clientes: Array.isArray(base.clientes) ? base.clientes : [],
    interaccionesWhatsapp: Array.isArray(base.interaccionesWhatsapp)
      ? base.interaccionesWhatsapp
      : [],
    cambiosDevoluciones: Array.isArray(base.cambiosDevoluciones)
      ? base.cambiosDevoluciones
      : [],
    creditosTienda: Array.isArray(base.creditosTienda) ? base.creditosTienda : [],
    promociones: Array.isArray(base.promociones) ? base.promociones : [],
    fidelizacionLedger: Array.isArray(base.fidelizacionLedger)
      ? base.fidelizacionLedger
      : [],
    referidos: Array.isArray(base.referidos) ? base.referidos : [],
    campanasSociales: Array.isArray(base.campanasSociales)
      ? base.campanasSociales
      : [],
    interesesCatalogo: Array.isArray(base.interesesCatalogo)
      ? base.interesesCatalogo
      : [],
    security: {
      ownerEmail: normalizeEmail(base?.security?.ownerEmail),
      allowedEmails: Array.isArray(base?.security?.allowedEmails)
        ? [...new Set(base.security.allowedEmails.map(normalizeEmail).filter(Boolean))]
        : [],
      roleByEmail:
        base?.security?.roleByEmail && typeof base.security.roleByEmail === "object"
          ? Object.fromEntries(
              Object.entries(base.security.roleByEmail)
                .map(([email, role]) => [normalizeEmail(email), cleanText(role).toUpperCase()])
                .filter(([email]) => !!email)
            )
          : {},
    },
  };
};

const estadoInicial = {
  schemaVersion: CURRENT_SCHEMA_VERSION,
  config: {
    nombreNegocio: "Mi Tienda de Ropa",
    moneda: "BOB",
    stockMinimo: 3,
    leadTimeReposicionDias: 7,
    puntosPorMoneda: 1,
    valorPunto: 0.1,
    bonoReferidoPuntos: 80,
    catalogoMostrarCodigoBarras: false,
  },
  productos: [],
  ventas: [],
  detalleVentas: [],
  compras: [],
  detalleCompras: [],
  movimientosInventario: [],
  clientes: [],
  interaccionesWhatsapp: [],
  cambiosDevoluciones: [],
  creditosTienda: [],
  promociones: [],
  fidelizacionLedger: [],
  referidos: [],
  campanasSociales: [],
  interesesCatalogo: [],
  security: {
    ownerEmail: "",
    allowedEmails: [],
    roleByEmail: {},
  },
};

// ---------------------------------------------------------------------------
// Entity key (X-Entidad / API) → state object key
// ---------------------------------------------------------------------------
const ENTITY_STATE_KEYS = {
  config:                     "config",
  security:                   "security",
  productos:                  "productos",
  ventas:                     "ventas",
  "detalle-ventas":           "detalleVentas",
  compras:                    "compras",
  "detalle-compras":          "detalleCompras",
  movimientos:                "movimientosInventario",
  clientes:                   "clientes",
  "interacciones-whatsapp":   "interaccionesWhatsapp",
  "cambios-devoluciones":     "cambiosDevoluciones",
  "creditos-tienda":          "creditosTienda",
  promociones:                "promociones",
  fidelizacion:               "fidelizacionLedger",
  referidos:                  "referidos",
  campanas:                   "campanasSociales",
  "intereses-catalogo":       "interesesCatalogo",
};

const ALL_ENTITY_KEYS = Object.keys(ENTITY_STATE_KEYS);
const SINGLE_ENTITY_KEYS = new Set(["config", "security"]);

// Build initial state from per-entity caches (or fall back to defaults).
const loadStateFromCache = () => {
  const input = {};
  for (const [entidad, stateKey] of Object.entries(ENTITY_STATE_KEYS)) {
    const cached = retailPosCache.getEntity(entidad);
    if (cached !== null) {
      input[stateKey] = cached;
    }
  }
  return Object.keys(input).length > 0 ? normalizeState(input) : normalizeState(estadoInicial);
};

export function useRetailStore() {
  const socketRef = useRef(null);
  const [socketCycle, setSocketCycle] = useState(0);
  const [syncStatus, setSyncStatus] = useState("loading");
  const [pendingCount, setPendingCount] = useState(() => retailPosOfflineQueue.list().length);
  // 'idle' | 'flushing' | 'failed'
  const [flushingStatus, setFlushingStatus] = useState("idle");
  const [flushError, setFlushError] = useState(null); // { transactionId, action, error }
  const getAuthToken = useCallback(() => cleanText(localStorage.getItem("token")), []);

  const [state, setState] = useState(() => {
    try {
      return loadStateFromCache();
    } catch {
      return normalizeState(estadoInicial);
    }
  });

  // Apply a full state replacement (used by legacy SYNC or socket full-state events).
  const applyFullState = useCallback((rawState) => {
    const nextState = normalizeState(rawState || estadoInicial);
    setState(nextState);
    // Persist each entity individually in cache.
    for (const [entidad, stateKey] of Object.entries(ENTITY_STATE_KEYS)) {
      retailPosCache.setEntity(entidad, nextState[stateKey]);
    }
  }, []);

  // Merge delta changes returned by a transaction or socket event.
  // Only the entities present in `changes` are updated.
  const applyChanges = useCallback((changes) => {
    if (!changes || Object.keys(changes).length === 0) return;
    setState((prev) => {
      const next = { ...prev };
      for (const [entidad, stateKey] of Object.entries(ENTITY_STATE_KEYS)) {
        const changed = changes[entidad];
        if (changed === undefined) continue;
        if (SINGLE_ENTITY_KEYS.has(entidad)) {
          next[stateKey] = { ...(prev[stateKey] || {}), ...changed };
        } else if (Array.isArray(changed) && changed.length > 0) {
          const map = new Map((prev[stateKey] || []).map((e) => [e.id, e]));
          for (const item of changed) map.set(item.id, item);
          next[stateKey] = [...map.values()];
        }
        retailPosCache.setEntity(entidad, next[stateKey]);
      }
      return normalizeState(next);
    });
  }, []);

  // Apply an optimistic local state update immediately when a transaction is queued offline.
  // Items are tagged with _pending: true and use transactionId as their temporary id.
  // They are replaced by authoritative server data once connectivity is restored.
  const applyOptimisticUpdate = useCallback((action, payload, transactionId) => {
    setState((prev) => {
      const next = { ...prev };
      const now = nowISO();

      switch (action) {
        case "registrarVenta": {
          const { items = [], metodoPago, metadata = {} } = payload;
          const ventaFecha = resolveFechaRegistro(
            metadata.fecha || metadata.fechaRegistro || payload.fecha,
            new Date(now)
          );
          const descuentosAplicados = Array.isArray(metadata.descuentos) ? metadata.descuentos : [];
          const subtotal = calculateSubtotal(items);
          const descuentoDesdeDetalle = descuentosAplicados.reduce(
            (acc, d) => acc + Math.max(0, parseFloat(d?.monto) || 0),
            0
          );
          const descuentoTotal = Math.max(
            0,
            parseFloat(metadata.descuento_total ?? metadata.descuentoTotal) || descuentoDesdeDetalle
          );
          const total = Math.max(0, subtotal - descuentoTotal);
          next.ventas = [
            ...(prev.ventas || []),
            {
              id: transactionId,
              fecha: ventaFecha,
              subtotal,
              total,
              descuento_total: descuentoTotal,
              descuentos: descuentosAplicados,
              metodo_pago: metodoPago,
              metodo_pago_detalle: metadata.metodoPagoDetalle || metadata.metodo_pago_detalle || null,
              cliente_id: metadata.cliente_id || metadata.clienteId || null,
              codigo_campana: metadata.codigo_campana || metadata.codigoCampana || null,
              campana_id: metadata.campana_id || null,
              anulado: false,
              margen_bruto: 0,
              _pending: true,
            },
          ];
          next.detalleVentas = [
            ...(prev.detalleVentas || []),
            ...items.map((item) => ({
              id: `${transactionId}-${item.producto_id}`,
              venta_id: transactionId,
              producto_id: item.producto_id,
              nombre: item.nombre || "",
              cantidad: item.cantidad,
              precio_unitario: item.precio_unitario,
              costo_unitario: item.costo_unitario || 0,
              categoria: item.categoria || "",
              talla: item.talla || "",
              color: item.color || "",
              _pending: true,
            })),
          ];
          next.productos = (prev.productos || []).map((p) => {
            const item = items.find((i) => i.producto_id === p.id);
            if (!item) return p;
            return { ...p, stock: Math.max(0, p.stock - (parseInt(item.cantidad) || 0)) };
          });
          retailPosCache.setEntity("ventas", next.ventas);
          retailPosCache.setEntity("detalle-ventas", next.detalleVentas);
          retailPosCache.setEntity("productos", next.productos);
          break;
        }

        case "anularVenta": {
          next.ventas = (prev.ventas || []).map((v) =>
            v.id === payload.ventaId ? { ...v, anulado: true, _pending: true } : v
          );
          retailPosCache.setEntity("ventas", next.ventas);
          break;
        }

        case "registrarCompra": {
          const { proveedor, items = [], fecha: fechaCompra, fechaRegistro } = payload;
          const compraFecha = resolveFechaRegistro(fechaCompra || fechaRegistro, new Date(now));
          const total = items.reduce(
            (acc, i) => acc + (parseInt(i.cantidad) || 0) * (parseFloat(i.precio_compra) || 0),
            0
          );
          next.compras = [
            ...(prev.compras || []),
            { id: transactionId, proveedor: proveedor || "", fecha: compraFecha, total, _pending: true },
          ];
          next.detalleCompras = [
            ...(prev.detalleCompras || []),
            ...items.map((item, idx) => ({
              id: `${transactionId}-${idx}`,
              compra_id: transactionId,
              producto_id: item.producto_id,
              nombre: item.nombre || "",
              cantidad: item.cantidad,
              precio_compra: parseFloat(item.precio_compra) || 0,
              _pending: true,
            })),
          ];
          next.productos = (prev.productos || []).map((p) => {
            const item = items.find((i) => i.producto_id === p.id);
            if (!item) return p;
            return { ...p, stock: p.stock + (parseInt(item.cantidad) || 0) };
          });
          retailPosCache.setEntity("compras", next.compras);
          retailPosCache.setEntity("detalle-compras", next.detalleCompras);
          retailPosCache.setEntity("productos", next.productos);
          break;
        }

        case "agregarProducto": {
          const cat  = cleanText(payload.categoria || "GENERAL");
          const temp = cleanText(payload.temporada || "BASE");
          const col  = cleanText(payload.color || "");
          const tall = cleanText(payload.talla || "");
          const cort = cleanText(payload.corte || "REGULAR");
          const nom  = cleanText(payload.nombre || "");
          next.productos = [
            ...(prev.productos || []),
            {
              id: transactionId,
              nombre: nom,
              marca: cleanText(payload.marca || ""),
              codigo_barras: cleanText(payload.codigo_barras || ""),
              sku: cleanText(payload.sku) || createSku({ categoria: cat, nombre: nom, temporada: temp, color: col, talla: tall, corte: cort }),
              producto_base: cleanText(payload.producto_base || nom),
              categoria: cat,
              temporada: temp,
              talla: tall,
              color: col,
              corte: cort,
              precio_compra: parseFloat(payload.precio_compra) || 0,
              precio_venta: parseFloat(payload.precio_venta) || 0,
              precio_minimo: parseFloat(payload.precio_minimo) || 0,
              imagenes: normalizeProductoImagenes(payload),
              imagenKey: cleanText(payload.imagenKey || ""),
              imagenUrl: cleanText(payload.imagenUrl || ""),
              mostrarEnCatalogo: payload.mostrarEnCatalogo !== false,
              stock: parseInt(payload.stock) || 0,
              stock_reservado: 0,
              activo: true,
              fecha_creacion: now,
              _pending: true,
            },
          ];
          retailPosCache.setEntity("productos", next.productos);
          break;
        }

        case "editarProducto": {
          const { id, cambios } = payload;
          next.productos = (prev.productos || []).map((p) =>
            p.id === id ? { ...p, ...cambios, _pending: true } : p
          );
          retailPosCache.setEntity("productos", next.productos);
          break;
        }

        case "desactivarProducto": {
          next.productos = (prev.productos || []).map((p) =>
            p.id === payload.id ? { ...p, activo: false, _pending: true } : p
          );
          retailPosCache.setEntity("productos", next.productos);
          break;
        }

        case "upsertCliente": {
          const clienteId = payload.id || transactionId;
          const map = new Map((prev.clientes || []).map((c) => [c.id, c]));
          map.set(clienteId, { ...(map.get(clienteId) || {}), ...payload, id: clienteId, _pending: true });
          next.clientes = [...map.values()];
          retailPosCache.setEntity("clientes", next.clientes);
          break;
        }

        case "guardarPromocion": {
          const promoId = payload.id || transactionId;
          const map = new Map((prev.promociones || []).map((p) => [p.id, p]));
          map.set(promoId, { ...(map.get(promoId) || {}), ...payload, id: promoId, _pending: true });
          next.promociones = [...map.values()];
          retailPosCache.setEntity("promociones", next.promociones);
          break;
        }

        case "registrarCanjeFidelizacion": {
          next.fidelizacionLedger = [
            ...(prev.fidelizacionLedger || []),
            {
              id: transactionId,
              cliente_id: payload.cliente_id,
              puntos: -(Math.abs(parseInt(payload.puntos) || 0)),
              tipo: "CANJE",
              motivo: payload.motivo || "",
              fecha: now,
              _pending: true,
            },
          ];
          retailPosCache.setEntity("fidelizacion", next.fidelizacionLedger);
          break;
        }

        case "registrarReferido": {
          next.referidos = [
            ...(prev.referidos || []),
            { ...payload, id: transactionId, fecha: now, estado: "PENDIENTE", _pending: true },
          ];
          retailPosCache.setEntity("referidos", next.referidos);
          break;
        }

        case "actualizarEstadoReferido": {
          next.referidos = (prev.referidos || []).map((r) =>
            r.id === payload.referido_id
              ? { ...r, estado: payload.estado, monto_compra: payload.monto_compra, _pending: true }
              : r
          );
          retailPosCache.setEntity("referidos", next.referidos);
          break;
        }

        case "registrarCampanaSocial": {
          next.campanasSociales = [
            ...(prev.campanasSociales || []),
            { ...payload, id: transactionId, fecha: now, estado: "ACTIVA", _pending: true },
          ];
          retailPosCache.setEntity("campanas", next.campanasSociales);
          break;
        }

        case "actualizarEstadoCampana": {
          const { campana_id, codigo, estado } = payload;
          next.campanasSociales = (prev.campanasSociales || []).map((c) => {
            if (campana_id && c.id === campana_id) return { ...c, estado, _pending: true };
            if (codigo && normalizeCampaignCode(c.codigo) === normalizeCampaignCode(codigo))
              return { ...c, estado, _pending: true };
            return c;
          });
          retailPosCache.setEntity("campanas", next.campanasSociales);
          break;
        }

        case "vincularInteresCatalogo": {
          const anchor = (prev.interesesCatalogo || []).find(
            (i) => i.id === payload.interes_id
          );
          const shopperPhone = normalizeFullPhone(anchor?.shopperWhatsapp);
          next.interesesCatalogo = (prev.interesesCatalogo || []).map((i) => {
            if (!anchor || !isSameCatalogVisitor(i, anchor)) return i;
            const patch = {
              ...i,
              cliente_id: payload.cliente_id,
              vinculado_at: now,
              vinculado_por: payload.vinculado_por || "",
              _pending: true,
            };
            const rowPhone = normalizeFullPhone(i.shopperWhatsapp) || shopperPhone;
            if (rowPhone) patch.shopperWhatsapp = rowPhone;
            return patch;
          });
          if (shopperPhone && payload.cliente_id) {
            next.clientes = (prev.clientes || []).map((c) => {
              if (c.id !== payload.cliente_id) return c;
              const cur = normalizeFullPhone(c.whatsapp);
              if (cur && cur.length >= 10) return c;
              return {
                ...c,
                whatsapp: shopperPhone,
                consentimiento_whatsapp: c.consentimiento_whatsapp !== false,
                ultima_actualizacion: now,
                _pending: true,
              };
            });
            retailPosCache.setEntity("clientes", next.clientes);
          }
          retailPosCache.setEntity("intereses-catalogo", next.interesesCatalogo);
          break;
        }

        case "desvincularInteresCatalogo": {
          next.interesesCatalogo = (prev.interesesCatalogo || []).map((i) =>
            i.id === payload.interes_id
              ? {
                  ...i,
                  cliente_id: null,
                  vinculado_at: null,
                  vinculado_por: null,
                  _pending: true,
                }
              : i
          );
          retailPosCache.setEntity("intereses-catalogo", next.interesesCatalogo);
          break;
        }

        case "actualizarConfig": {
          next.config = { ...(prev.config || {}), ...payload };
          retailPosCache.setEntity("config", next.config);
          break;
        }

        case "agregarCorreoPermitido": {
          const email = normalizeEmail(payload.email || "");
          if (email) {
            next.security = {
              ...(prev.security || {}),
              allowedEmails: [...new Set([...(prev.security?.allowedEmails || []), email])],
            };
            retailPosCache.setEntity("security", next.security);
          }
          break;
        }

        case "removerCorreoPermitido": {
          const email = normalizeEmail(payload.email || "");
          next.security = {
            ...(prev.security || {}),
            allowedEmails: (prev.security?.allowedEmails || []).filter((e) => e !== email),
          };
          retailPosCache.setEntity("security", next.security);
          break;
        }

        case "asignarRolCorreo": {
          const email = normalizeEmail(payload.email || "");
          const rol = cleanText(payload.rol || "").toUpperCase();
          next.security = {
            ...(prev.security || {}),
            roleByEmail: { ...(prev.security?.roleByEmail || {}), [email]: rol },
          };
          retailPosCache.setEntity("security", next.security);
          break;
        }

        case "registrarInteraccionWhatsapp":
        case "enviarCampanaWhatsapp": {
          next.interaccionesWhatsapp = [
            ...(prev.interaccionesWhatsapp || []),
            { ...payload, id: transactionId, fecha: now, _pending: true },
          ];
          retailPosCache.setEntity("interacciones-whatsapp", next.interaccionesWhatsapp);
          break;
        }

        case "registrarCambioDevolucion": {
          next.cambiosDevoluciones = [
            ...(prev.cambiosDevoluciones || []),
            { ...payload, id: transactionId, fecha: now, _pending: true },
          ];
          retailPosCache.setEntity("cambios-devoluciones", next.cambiosDevoluciones);
          break;
        }

        default:
          break;
      }

      return next;
    });
  }, []);

  const flushPendingTransactions = useCallback(async () => {
    if (!getAuthToken()) {
      setPendingCount(retailPosOfflineQueue.list().length);
      return { pending: retailPosOfflineQueue.list().length };
    }

    if (isDefinitelyOffline()) {
      const pending = retailPosOfflineQueue.list().length;
      setPendingCount(pending);
      return { ok: false, pending };
    }

    if (retailPosOfflineQueue.list().length === 0) {
      return { ok: true, pending: 0 };
    }

    setFlushingStatus("flushing");
    setFlushError(null);

    const tempToRealProductId = {};

    const result = await retailPosOfflineQueue.flush(
      async (entry) => {
        const payloadWithIds = remapProductIdsInPayload(
          entry.action,
          entry.payload,
          tempToRealProductId
        );
        const normalizedEntry =
          entry.action === "agregarProducto"
            ? {
                ...entry,
                payload: {
                  ...payloadWithIds,
                  id: cleanText(payloadWithIds?.id) || entry.transactionId,
                },
              }
            : { ...entry, payload: payloadWithIds };

        const response = await retailPosApi.transact(normalizedEntry);
        if (!response?.result?.ok) {
          const reason = cleanText(response?.result?.reason || "Transacción rechazada por backend");
          const businessError = new Error(reason);
          businessError.code = "ERR_TRANSACTION_REJECTED";
          businessError.transactionAction = entry.action;
          businessError.transactionId = entry.transactionId;
          throw businessError;
        }
        if (entry.action === "agregarProducto") {
          const tempId = cleanText(entry.transactionId);
          const realId = cleanText(response?.result?.id);
          if (tempId && realId && tempId !== realId) {
            tempToRealProductId[tempId] = realId;
          }
        }
        if (response?.changes) applyChanges(response.changes);
      },
      isOfflineError
    );

    setPendingCount(result.pending);

    if (result.ok) {
      setFlushingStatus("idle");
      setFlushError(null);
    } else {
      setFlushingStatus("failed");
      // Find the action name of the failed transaction so the UI can show context.
      const failedEntry = retailPosOfflineQueue
        .list()
        .find((e) => e.transactionId === result.failedTransactionId);
      setFlushError({
        transactionId: result.failedTransactionId,
        action: failedEntry?.action || null,
        error: result.lastError,
      });
    }

    return result;
  }, [applyChanges, getAuthToken]);

  // Fetch all entities from the backend in parallel.
  const syncAllEntities = useCallback(async () => {
    if (!getAuthToken()) {
      setSyncStatus("unauthorized");
      return false;
    }

    if (isDefinitelyOffline()) {
      setSyncStatus("offline");
      return false;
    }

    try {
      // Flush any queued offline transactions first so the server is up-to-date
      // before we fetch its state. This eliminates the flicker where optimistic
      // items temporarily disappear during the sync-then-flush sequence.
      await flushPendingTransactions();

      const entries = Object.entries(ENTITY_STATE_KEYS);
      const results = await Promise.allSettled(
        entries.map(([entidad]) => retailPosApi.getEntity(entidad))
      );

      const subscriptionDenied = results.find(
        (result) => result.status === "rejected" && isSubscriptionDeniedError(result.reason)
      );
      if (subscriptionDenied) {
        setSyncStatus("subscription_expired");
        return false;
      }

      const stateInput = {};
      entries.forEach(([entidad, stateKey], i) => {
        const r = results[i];
        if (r.status === "fulfilled" && r.value !== undefined) {
          stateInput[stateKey] = r.value;
          retailPosCache.setEntity(entidad, r.value);
        }
      });

      setState(normalizeState(stateInput));
      setSyncStatus("online");
      return true;
    } catch (error) {
      if (isOfflineError(error)) { setSyncStatus("offline"); return false; }
      if (error?.response?.status === 401) { setSyncStatus("unauthorized"); return false; }
      setSyncStatus("error");
      throw error;
    }
  }, [flushPendingTransactions, getAuthToken]);

  const refreshInteresesCatalogo = useCallback(async () => {
    if (!getAuthToken() || isDefinitelyOffline()) return false;
    try {
      const data = await retailPosApi.getEntity("intereses-catalogo");
      const list = Array.isArray(data) ? data : [];
      retailPosCache.setEntity("intereses-catalogo", list);
      setState((prev) => normalizeState({ ...prev, interesesCatalogo: list }));
      return true;
    } catch {
      return false;
    }
  }, [getAuthToken]);

  const executeTransaction = useCallback(
    async (action, payload = {}, { noOfflineQueue = false } = {}) => {
      if (!getAuthToken()) {
        setSyncStatus("unauthorized");
        return { ok: false, unauthorized: true, reason: "Sesion no iniciada" };
      }

      const entry = {
        transactionId: uuid(),
        action,
        payload,
        clientTimestamp: new Date().toISOString(),
      };
      if (action === "agregarProducto") {
        entry.payload = {
          ...(payload || {}),
          id: cleanText(payload?.id) || entry.transactionId,
        };
      }

      // Proactive check: if already offline, skip the network request entirely.
      if (isDefinitelyOffline()) {
        setSyncStatus("offline");
        if (!noOfflineQueue) {
          retailPosOfflineQueue.enqueue(entry);
          setPendingCount(retailPosOfflineQueue.list().length);
          applyOptimisticUpdate(action, payload, entry.transactionId);
        }
        return { ok: false, queued: !noOfflineQueue, offline: true, reason: "Sin conexión, transacción en cola" };
      }

      try {
        const response = await retailPosApi.transact(entry);
        if (response?.changes) applyChanges(response.changes);
        setSyncStatus("online");
        return response?.result || { ok: true };
      } catch (error) {
        if (error?.response?.status === 401) {
          setSyncStatus("unauthorized");
          return { ok: false, unauthorized: true, reason: "Sesion expirada" };
        }
        if (!isOfflineError(error) && !isRecoverableSyncError(error)) throw error;

        if (!noOfflineQueue) {
          retailPosOfflineQueue.enqueue(entry);
          setPendingCount(retailPosOfflineQueue.list().length);
          applyOptimisticUpdate(action, payload, entry.transactionId);
        }
        // Force local-offline mode so next calls are short-circuited until reconnect.
        markKnownOffline();
        setSyncStatus("offline");
        return { ok: false, queued: !noOfflineQueue, reason: "Sin conexión, transacción en cola" };
      }
    },
    [applyChanges, applyOptimisticUpdate, getAuthToken]
  );

  const costoPromedioPorProducto = useMemo(
    () => buildAverageCostMap(state.productos, state.detalleCompras),
    [state.productos, state.detalleCompras]
  );

  useEffect(() => {
    if (isDefinitelyOffline()) {
      setSyncStatus("offline");
      return;
    }
    const hasPending = retailPosOfflineQueue.list().length > 0;
    // Skip network round-trip if every entity cache is fresh and nothing is queued.
    const allFresh = !hasPending && ALL_ENTITY_KEYS.every((e) => retailPosCache.isEntityFresh(e));
    if (allFresh) {
      setSyncStatus("online");
      return;
    }
    syncAllEntities().catch(() => setSyncStatus("error"));
  }, [syncAllEntities]);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setSyncStatus("unauthorized");
      return undefined;
    }
    if (isDefinitelyOffline()) {
      setSyncStatus("offline");
      return undefined;
    }

    const socket = connectRetailPosSocket({
      token,
      onSnapshot: (payload) => {
        // New format: { changes, transactionId }
        // Legacy format: { state, ... }  (backward compat)
        if (payload?.changes) {
          applyChanges(payload.changes);
        } else if (payload?.state) {
          applyFullState(payload.state);
        }
        setSyncStatus("online");
      },
      onConnect: () => setSyncStatus("online"),
      onDisconnect: () => setSyncStatus("offline"),
      onError: () => setSyncStatus("offline"),
    });

    socketRef.current = socket;
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [applyChanges, applyFullState, getAuthToken, socketCycle]);

  useEffect(() => {
    const onOnline = () => {
      setSocketCycle((n) => n + 1);
      syncAllEntities().catch(() => {
        setSyncStatus("error");
      });
    };
    const onOffline = () => {
      setSyncStatus("offline");
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [syncAllEntities]);

  /* =========================
     CONFIGURACIÓN
  ========================== */

  const actualizarConfig = useCallback(
    async (config) => {
      const result = await executeTransaction("actualizarConfig", config);
      return !!result?.ok;
    },
    [executeTransaction]
  );

  /* =========================
     PRODUCTOS (INVENTARIO)
  ========================== */

  const agregarProducto = useCallback(
    async (producto) => {
      const result = await executeTransaction("agregarProducto", producto);
      return result?.id || false;
    },
    [executeTransaction]
  );

  const editarProducto = useCallback(
    async (id, cambios) => {
      const result = await executeTransaction("editarProducto", { id, cambios });
      return !!result?.ok;
    },
    [executeTransaction]
  );

  const desactivarProducto = useCallback(
    async (id) => {
      const result = await executeTransaction("desactivarProducto", { id });
      return !!result?.ok;
    },
    [executeTransaction]
  );

  const buscarProductoPorCodigo = useCallback(
    (codigo) => {
      return state.productos.find(
        (p) => (p.codigo_barras === codigo || p.sku === codigo) && p.activo
      );
    },
    [state.productos]
  );

  const buscarProductos = useCallback(
    (termino) => {
      if (!termino) return state.productos.filter((p) => p.activo);
      const t = termino.toLowerCase();
      return state.productos.filter(
        (p) =>
          p.activo &&
          (p.nombre.toLowerCase().includes(t) ||
            String(p.marca || "").toLowerCase().includes(t) ||
            p.codigo_barras.toLowerCase().includes(t) ||
            String(p.sku || "").toLowerCase().includes(t) ||
            String(p.categoria || "").toLowerCase().includes(t) ||
            String(p.temporada || "").toLowerCase().includes(t) ||
            String(p.color || "").toLowerCase().includes(t) ||
            String(p.talla || "").toLowerCase().includes(t))
      );
    },
    [state.productos]
  );

  const buscarProductosInactivos = useCallback(
    (termino) => {
      if (!termino) return state.productos.filter((p) => !p.activo);
      const t = termino.toLowerCase();
      return state.productos.filter(
        (p) =>
          !p.activo &&
          (p.nombre.toLowerCase().includes(t) ||
            String(p.marca || "").toLowerCase().includes(t) ||
            p.codigo_barras.toLowerCase().includes(t) ||
            String(p.sku || "").toLowerCase().includes(t) ||
            String(p.categoria || "").toLowerCase().includes(t) ||
            String(p.temporada || "").toLowerCase().includes(t) ||
            String(p.color || "").toLowerCase().includes(t) ||
            String(p.talla || "").toLowerCase().includes(t))
      );
    },
    [state.productos]
  );

  /* =========================
     CLIENTES / CRM
  ========================== */

  const upsertCliente = useCallback(
    async (cliente) => {
      const phone = normalizeFullPhone(cliente?.whatsapp);
      if (!isValidFullPhone(phone)) return null;
      const result = await executeTransaction("upsertCliente", {
        ...cliente,
        whatsapp: phone,
      });
      return result?.id || null;
    },
    [executeTransaction]
  );

  const registrarInteraccionWhatsapp = useCallback(
    async (interaccion) => {
      const result = await executeTransaction("registrarInteraccionWhatsapp", interaccion);
      return !!result?.ok;
    },
    [executeTransaction]
  );

  const enviarCampanaWhatsapp = useCallback(
    async ({ cliente_id, tipo, plantilla, metadata = {}, forzar = false }) => {
      return executeTransaction("enviarCampanaWhatsapp", {
        cliente_id,
        tipo,
        plantilla,
        metadata,
        forzar,
      });
    },
    [executeTransaction]
  );

  /* =========================
     VENTAS (POS)
  ========================== */

  const registrarVenta = useCallback(
    async (items, metodoPago, metadata = {}) => {
      const result = await executeTransaction("registrarVenta", {
        items,
        metodoPago,
        metadata,
      });
      if (result?.ventaId) return { ok: true, ventaId: result.ventaId };
      if (result?.queued) return { ok: false, queued: true, reason: result.reason };
      return { ok: false, queued: false };
    },
    [executeTransaction]
  );

  const anularVenta = useCallback(
    async (ventaId) => {
      const result = await executeTransaction("anularVenta", { ventaId });
      return !!result?.ok;
    },
    [executeTransaction]
  );

  /* =========================
     COMPRAS (INGRESO INVENTARIO)
  ========================== */

  const registrarCompra = useCallback(
    async (proveedor, items, options = {}) => {
      const result = await executeTransaction("registrarCompra", {
        proveedor,
        items,
        fecha: options.fecha || options.fechaRegistro || null,
      });
      if (result?.compraId) return { ok: true, compraId: result.compraId, queued: false };
      if (result?.queued) return { ok: false, queued: true, reason: result.reason };
      return { ok: false, queued: false };
    },
    [executeTransaction]
  );

  /* =========================
     CAMBIOS / DEVOLUCIONES
  ========================== */

  const validarCambioDevolucion = useCallback(
    (payload) => {
      const ventaId = cleanText(payload.venta_id);
      const tipo = cleanText(payload.tipo || "CAMBIO_TALLA");
      const cantidad = Math.max(1, parseInt(payload.cantidad) || 1);
      if (!ventaId) return { ok: false, reason: "Venta requerida" };

      const venta = state.ventas.find((v) => v.id === ventaId);
      if (!venta || venta.anulado) {
        return { ok: false, reason: "Venta inválida o anulada" };
      }

      const policy = evaluateChangePolicy({
        ventaFecha: venta.fecha,
        tipo,
        estadoPrenda: payload.estado_prenda,
      });
      if (!policy.ok) return policy;

      const originId = cleanText(payload.producto_origen_id);
      const destinationId = cleanText(payload.producto_destino_id);
      const originDetails = state.detalleVentas.filter(
        (d) => d.venta_id === ventaId && d.producto_id === originId
      );
      const soldQty = originDetails.reduce(
        (acc, d) => acc + (parseInt(d.cantidad) || 0),
        0
      );
      if (originId && soldQty < cantidad) {
        return { ok: false, reason: "Cantidad supera lo vendido en el ticket" };
      }

      if (destinationId) {
        const destinationProduct = state.productos.find((p) => p.id === destinationId);
        if (!destinationProduct || !destinationProduct.activo) {
          return { ok: false, reason: "Producto destino no disponible" };
        }
        if (destinationProduct.stock < cantidad) {
          return { ok: false, reason: "Stock insuficiente en producto destino" };
        }
      }

      return { ok: true, ...policy };
    },
    [state.detalleVentas, state.productos, state.ventas]
  );

  const registrarCambioDevolucion = useCallback(
    async (payload) => {
      const validation = validarCambioDevolucion(payload);
      if (!validation.ok) return false;
      const result = await executeTransaction("registrarCambioDevolucion", payload);
      return result?.id || false;
    },
    [executeTransaction, validarCambioDevolucion]
  );

  /* =========================
     PROMOCIONES
  ========================== */

  const guardarPromocion = useCallback(
    async (promo) => {
      const result = await executeTransaction("guardarPromocion", promo);
      return result?.id || null;
    },
    [executeTransaction]
  );

  const promocionesActivas = useMemo(() => {
    const now = Date.now();
    return (state.promociones || [])
      .filter((p) => p.activa)
      .filter((p) => {
        const inicio = p.fecha_inicio ? new Date(p.fecha_inicio).getTime() : null;
        const fin = p.fecha_fin ? new Date(p.fecha_fin).getTime() : null;
        if (inicio && now < inicio) return false;
        if (fin && now > fin) return false;
        return true;
      })
      .sort((a, b) => (parseInt(a.prioridad) || 50) - (parseInt(b.prioridad) || 50));
  }, [state.promociones]);

  const evaluarPromocionesCarrito = useCallback(
    (items = [], contexto = {}) => {
      const subtotal = calculateSubtotal(items);
      const avgCostMap = buildAverageCostMap(state.productos, state.detalleCompras);
      const costoTotal = items.reduce((acc, i) => {
        const qty = Math.max(0, parseInt(i.cantidad) || 0);
        const costoPromedio = parseFloat(avgCostMap[i.producto_id]);
        const fallback = Math.max(0, parseFloat(i.costo_unitario) || 0);
        const cost = Number.isFinite(costoPromedio) ? costoPromedio : fallback;
        return acc + qty * cost;
      }, 0);
      const channel = cleanText(contexto.canal || "TIENDA");
      const codigoCampana = normalizeCampaignCode(
        contexto.codigoCampana || contexto.codigo_campana
      );
      const campanaCupon = codigoCampana
        ? (state.campanasSociales || []).find(
            (c) =>
              normalizeCampaignCode(c.codigo) === codigoCampana &&
              cleanText(c.estado || "ACTIVA") !== "INACTIVA"
          )
        : null;
      const applied = [];

      if (subtotal <= 0 || items.length === 0) {
        return {
          subtotal,
          descuentoTotal: 0,
          totalFinal: subtotal,
          margenPct: 0,
          promocionesAplicadas: applied,
        };
      }

      const itemQty = items.reduce((acc, i) => acc + (parseInt(i.cantidad) || 0), 0);

      const lastSaleByProduct = {};
      const activeSaleIds = new Set(
        state.ventas.filter((v) => !v.anulado).map((v) => v.id)
      );
      state.detalleVentas.forEach((d) => {
        if (!activeSaleIds.has(d.venta_id)) return;
        const venta = state.ventas.find((v) => v.id === d.venta_id);
        if (!venta) return;
        const prev = lastSaleByProduct[d.producto_id];
        if (!prev || new Date(venta.fecha) > new Date(prev)) {
          lastSaleByProduct[d.producto_id] = venta.fecha;
        }
      });

      let descuentoAcumulado = 0;

      if (campanaCupon?.descuento) {
        const tipoDescuento = cleanText(campanaCupon.descuento.tipo).toUpperCase();
        const valorDescuento = Math.max(0, parseFloat(campanaCupon.descuento.valor) || 0);
        let descuentoCampana = 0;

        if (tipoDescuento === "PORCENTAJE") {
          descuentoCampana = subtotal * (valorDescuento / 100);
        } else if (tipoDescuento === "MONTO") {
          descuentoCampana = valorDescuento;
        }

        descuentoCampana = Math.min(subtotal, Math.max(0, descuentoCampana));
        if (descuentoCampana > 0) {
          descuentoAcumulado += descuentoCampana;
          applied.push({
            promo_id: `CUPON-${campanaCupon.id}`,
            nombre: `Cupón ${campanaCupon.codigo || codigoCampana}`,
            tipo: "CUPON_CAMPANA",
            monto: parseFloat(descuentoCampana.toFixed(2)),
          });
        }
      }

      promocionesActivas.forEach((promo) => {
        if (Array.isArray(promo.canales) && promo.canales.length > 0) {
          if (!promo.canales.includes(channel) && !promo.canales.includes("TODOS")) {
            return;
          }
        }

        const rules = promo.reglas || {};
        const pct = Math.max(0, parseFloat(rules.porcentaje) || 0);
        let descuentoPromo = 0;

        if (promo.tipo === "DESCUENTO_CATEGORIA") {
          const categoria = normalizeKey(rules.categoria || "");
          if (!categoria || pct <= 0) return;
          const subtotalCat = items
            .filter((i) => normalizeKey(i.categoria) === categoria)
            .reduce(
              (acc, i) =>
                acc + (parseInt(i.cantidad) || 0) * (parseFloat(i.precio_unitario) || 0),
              0
            );
          descuentoPromo = subtotalCat * (pct / 100);
        }

        if (promo.tipo === "DESCUENTO_VOLUMEN") {
          const minCantidad = Math.max(2, parseInt(rules.minCantidad) || 2);
          if (itemQty < minCantidad || pct <= 0) return;
          descuentoPromo = subtotal * (pct / 100);
        }

        if (promo.tipo === "ROTACION_LENTA") {
          const minDias = Math.max(7, parseInt(rules.minDiasSinVenta) || 30);
          if (pct <= 0) return;
          const subtotalLento = items.reduce((acc, i) => {
            const lastSale = lastSaleByProduct[i.producto_id];
            const days = lastSale
              ? (Date.now() - new Date(lastSale).getTime()) / MS_DAY
              : 999;
            if (days >= minDias) {
              return (
                acc +
                (parseInt(i.cantidad) || 0) * (parseFloat(i.precio_unitario) || 0)
              );
            }
            return acc;
          }, 0);
          descuentoPromo = subtotalLento * (pct / 100);
        }

        if (promo.tipo === "COMBO_OUTFIT") {
          const categorias = Array.isArray(rules.categorias)
            ? rules.categorias.map((c) => normalizeKey(c)).filter(Boolean)
            : [];
          if (categorias.length === 0) return;

          const cartCats = new Set(items.map((i) => normalizeKey(i.categoria)));
          const cumple = categorias.every((cat) => cartCats.has(cat));
          if (!cumple) return;

          const montoFijo = Math.max(0, parseFloat(rules.montoFijo) || 0);
          descuentoPromo = montoFijo > 0 ? montoFijo : subtotal * (pct / 100);
        }

        if (descuentoPromo <= 0) return;

        const minMarginPct = Math.max(
          0,
          parseFloat(promo?.guardias?.margenMinimoPct) || 0
        );

        const projectedTotal = Math.max(0, subtotal - (descuentoAcumulado + descuentoPromo));
        const projectedMarginPct =
          projectedTotal > 0
            ? ((projectedTotal - costoTotal) / projectedTotal) * 100
            : 0;

        if (projectedMarginPct < minMarginPct) {
          return;
        }

        descuentoAcumulado += descuentoPromo;
        applied.push({
          promo_id: promo.id,
          nombre: promo.nombre,
          tipo: promo.tipo,
          monto: parseFloat(descuentoPromo.toFixed(2)),
        });
      });

      const descuentoTotal = Math.min(subtotal, parseFloat(descuentoAcumulado.toFixed(2)));
      const totalFinal = Math.max(0, subtotal - descuentoTotal);
      const margenPct = totalFinal > 0 ? ((totalFinal - costoTotal) / totalFinal) * 100 : 0;

      return {
        subtotal,
        descuentoTotal,
        totalFinal,
        margenPct,
        promocionesAplicadas: applied,
      };
    },
    [promocionesActivas, state.campanasSociales, state.detalleCompras, state.detalleVentas, state.productos, state.ventas]
  );

  const obtenerCostoPromedioProducto = useCallback(
    (productoId) => {
      const id = cleanText(productoId);
      if (!id) return 0;
      return Math.max(0, parseFloat(costoPromedioPorProducto[id]) || 0);
    },
    [costoPromedioPorProducto]
  );

  /* =========================
     REPORTES / MÉTRICAS
  ========================== */

  const ventasActivas = useMemo(
    () => state.ventas.filter((v) => !v.anulado),
    [state.ventas]
  );

  const ventasDelDia = useMemo(() => {
    const hoy = getLocalDateKey(new Date());
    return ventasActivas.filter((v) => getLocalDateKey(v.fecha) === hoy);
  }, [ventasActivas]);

  const ventasDelMes = useMemo(() => {
    const ahora = new Date();
    const mes = ahora.getMonth();
    const anio = ahora.getFullYear();
    return ventasActivas.filter((v) => {
      const d = new Date(v.fecha);
      return d.getMonth() === mes && d.getFullYear() === anio;
    });
  }, [ventasActivas]);

  const totalVentasDelDia = useMemo(
    () => ventasDelDia.reduce((acc, v) => acc + v.total, 0),
    [ventasDelDia]
  );

  const totalVentasDelMes = useMemo(
    () => ventasDelMes.reduce((acc, v) => acc + v.total, 0),
    [ventasDelMes]
  );

  const productosMasVendidos = useMemo(() => {
    const map = {};
    const ventasActivasMap = {};
    state.ventas.forEach((v) => {
      if (!v.anulado) ventasActivasMap[v.id] = v;
    });

    const detallesActivos = state.detalleVentas.filter((d) => !!ventasActivasMap[d.venta_id]);

    const brutoPorVenta = {};
    detallesActivos.forEach((d) => {
      const lineaBruta = (parseInt(d.cantidad) || 0) * (parseFloat(d.precio_unitario) || 0);
      brutoPorVenta[d.venta_id] = (brutoPorVenta[d.venta_id] || 0) + lineaBruta;
    });

    detallesActivos.forEach((d) => {
      if (!map[d.producto_id]) {
        map[d.producto_id] = { nombre: d.nombre, cantidad: 0, total: 0 };
      }

      const qty = parseInt(d.cantidad) || 0;
      const precio = parseFloat(d.precio_unitario) || 0;
      const lineaBruta = qty * precio;
      const venta = ventasActivasMap[d.venta_id];
      const descuentoVenta = Math.max(0, parseFloat(venta?.descuento_total) || 0);
      const brutoVenta = Math.max(0, parseFloat(brutoPorVenta[d.venta_id]) || 0);
      const proporcion = brutoVenta > 0 ? lineaBruta / brutoVenta : 0;
      const descuentoLinea = descuentoVenta * proporcion;
      const lineaNeta = Math.max(0, lineaBruta - descuentoLinea);

      map[d.producto_id].cantidad += qty;
      map[d.producto_id].total += lineaNeta;
    });

    return Object.entries(map)
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.cantidad - a.cantidad);
  }, [state.detalleVentas, state.ventas]);

  const productosBajoStock = useMemo(
    () =>
      state.productos
        .filter((p) => p.activo && p.stock <= state.config.stockMinimo)
        .sort((a, b) => a.stock - b.stock),
    [state.productos, state.config.stockMinimo]
  );

  const clienteStatsMap = useMemo(() => {
    const map = {};

    state.clientes.forEach((c) => {
      map[c.id] = defaultCustomerStats();
    });

    const activeSales = state.ventas.filter((v) => !v.anulado);

    activeSales.forEach((venta) => {
      const clienteId = cleanText(venta.cliente_id);
      if (!clienteId || !map[clienteId]) return;

      const stats = map[clienteId];
      stats.compras += 1;
      stats.montoAcumulado += parseFloat(venta.total) || 0;

      if (stats.ultimaCompra) {
        const prevDate = new Date(stats.ultimaCompra);
        const currentDate = new Date(venta.fecha);
        const diff = Math.max(0, (currentDate - prevDate) / (1000 * 60 * 60 * 24));
        stats.frecuenciaDias =
          stats.frecuenciaDias === null
            ? diff
            : (stats.frecuenciaDias * (stats.compras - 2) + diff) / (stats.compras - 1);
      }

      stats.ultimaCompra =
        !stats.ultimaCompra || new Date(venta.fecha) > new Date(stats.ultimaCompra)
          ? venta.fecha
          : stats.ultimaCompra;

      const saleDetails = state.detalleVentas.filter((d) => d.venta_id === venta.id);
      const categoryCounter = {};
      saleDetails.forEach((d) => {
        const category = cleanText(d.categoria || "GENERAL");
        categoryCounter[category] = (categoryCounter[category] || 0) + d.cantidad;
      });
      const categories = Object.entries(categoryCounter)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name]) => name);

      if (categories.length > 0) {
        const merged = [...stats.categoriasTop, ...categories];
        stats.categoriasTop = [...new Set(merged)].slice(0, 3);
      }
    });

    Object.keys(map).forEach((clienteId) => {
      const stats = map[clienteId];
      stats.ticketPromedio =
        stats.compras > 0 ? stats.montoAcumulado / stats.compras : 0;

      if (stats.montoAcumulado >= 3000 || stats.compras >= 8) {
        stats.segmento = "VIP";
      } else if (stats.compras >= 3) {
        stats.segmento = "FRECUENTE";
      } else if (stats.compras === 0) {
        stats.segmento = "NUEVO";
      } else {
        stats.segmento = "OCASIONAL";
      }

      if (!stats.ultimaCompra) {
        stats.riesgoChurn = "ALTO";
        return;
      }

      const daysFromLast =
        (Date.now() - new Date(stats.ultimaCompra).getTime()) /
        (1000 * 60 * 60 * 24);

      if (daysFromLast > 90) stats.riesgoChurn = "ALTO";
      else if (daysFromLast > 45) stats.riesgoChurn = "MEDIO";
      else stats.riesgoChurn = "BAJO";
    });

    return map;
  }, [state.clientes, state.detalleVentas, state.ventas]);

  const clientesSegmentados = useMemo(() => {
    return state.clientes
      .map((cliente) => ({
        ...cliente,
        stats: clienteStatsMap[cliente.id] || defaultCustomerStats(),
      }))
      .sort((a, b) => b.stats.montoAcumulado - a.stats.montoAcumulado);
  }, [clienteStatsMap, state.clientes]);

  const productosActivos = useMemo(
    () => state.productos.filter((p) => p.activo),
    [state.productos]
  );

  const variantesPorBase = useMemo(() => {
    const map = {};

    productosActivos.forEach((p) => {
      const baseKey = cleanText(p.producto_base || p.nombre || "SIN_BASE");
      if (!map[baseKey]) {
        map[baseKey] = {
          producto_base: baseKey,
          categoria: cleanText(p.categoria || "GENERAL"),
          temporada: cleanText(p.temporada || "BASE"),
          tallas: new Set(),
          colores: new Set(),
          variantes: [],
          totalStock: 0,
        };
      }

      map[baseKey].tallas.add(cleanText(p.talla || "UNICA"));
      map[baseKey].colores.add(cleanText(p.color || "SIN_COLOR"));
      map[baseKey].variantes.push({
        id: p.id,
        sku: p.sku,
        codigo_barras: p.codigo_barras,
        talla: cleanText(p.talla || "UNICA"),
        color: cleanText(p.color || "SIN_COLOR"),
        corte: cleanText(p.corte || "REGULAR"),
        stock: p.stock,
        stock_disponible: Math.max(0, p.stock - (p.stock_reservado || 0)),
        precio_venta: p.precio_venta,
      });
      map[baseKey].totalStock += p.stock;
    });

    return Object.values(map)
      .map((entry) => ({
        ...entry,
        tallas: [...entry.tallas].sort(),
        colores: [...entry.colores].sort(),
      }))
      .sort((a, b) => a.producto_base.localeCompare(b.producto_base));
  }, [productosActivos]);

  const reposicionSugerida = useMemo(() => {
    const today = Date.now();
    const last30d = state.ventas.filter((v) => {
      if (v.anulado) return false;
      return today - new Date(v.fecha).getTime() <= 30 * MS_DAY;
    });

    const ventaIds = new Set(last30d.map((v) => v.id));
    const qtySoldByProduct = {};
    state.detalleVentas.forEach((d) => {
      if (!ventaIds.has(d.venta_id)) return;
      qtySoldByProduct[d.producto_id] =
        (qtySoldByProduct[d.producto_id] || 0) + (parseInt(d.cantidad) || 0);
    });

    const leadDays = parseInt(state.config.leadTimeReposicionDias) || 7;

    return productosActivos
      .map((p) => {
        const sold30 = qtySoldByProduct[p.id] || 0;
        const avgDaily = sold30 / 30;
        const coverageDays = avgDaily > 0 ? p.stock / avgDaily : 999;
        const targetStock = Math.ceil(avgDaily * (leadDays + 7));
        const sugerido = Math.max(0, targetStock - p.stock);

        return {
          producto_id: p.id,
          nombre: p.nombre,
          producto_base: p.producto_base || p.nombre,
          sku: p.sku,
          talla: p.talla,
          color: p.color,
          categoria: p.categoria,
          stock: p.stock,
          vendido_30d: sold30,
          promedio_diario: avgDaily,
          cobertura_dias: coverageDays,
          sugerido,
          urgente:
            (coverageDays <= leadDays && sold30 > 0) ||
            (p.stock <= state.config.stockMinimo && sold30 > 0),
        };
      })
      .filter((r) => r.sugerido > 0 || r.urgente)
      .sort((a, b) => {
        if (a.urgente !== b.urgente) return a.urgente ? -1 : 1;
        return a.cobertura_dias - b.cobertura_dias;
      })
      .slice(0, 30);
  }, [productosActivos, state.config.leadTimeReposicionDias, state.config.stockMinimo, state.detalleVentas, state.ventas]);

  /* =========================
     GROWTH / FIDELIZACION
  ========================== */

  const puntosPorCliente = useMemo(() => {
    const map = {};
    (state.fidelizacionLedger || []).forEach((m) => {
      const clienteId = cleanText(m.cliente_id);
      if (!clienteId) return;
      if (!map[clienteId]) map[clienteId] = 0;
      map[clienteId] += parseInt(m.puntos) || 0;
    });
    return map;
  }, [state.fidelizacionLedger]);

  const clientesConGrowth = useMemo(() => {
    return clientesSegmentados.map((c) => {
      const puntos = puntosPorCliente[c.id] || 0;
      return {
        ...c,
        puntos,
        saldoMonetarioPuntos: puntos * (parseFloat(state.config.valorPunto) || 0.1),
      };
    });
  }, [clientesSegmentados, puntosPorCliente, state.config.valorPunto]);

  const registrarCanjeFidelizacion = useCallback(
    async ({ cliente_id, puntos, motivo }) => {
      const clienteId = cleanText(cliente_id);
      const puntosCanje = Math.max(1, parseInt(puntos) || 0);
      if (!clienteId || puntosCanje <= 0) return { ok: false, reason: "Datos inválidos" };

      const saldo = puntosPorCliente[clienteId] || 0;
      if (saldo < puntosCanje) {
        return { ok: false, reason: "Saldo de puntos insuficiente" };
      }

      return executeTransaction("registrarCanjeFidelizacion", {
        cliente_id: clienteId,
        puntos: puntosCanje,
        motivo,
      });
    },
    [executeTransaction, puntosPorCliente]
  );

  const registrarReferido = useCallback(
    ({ cliente_origen_id, nombre_referido, telefono_referido }) => {
      return executeTransaction("registrarReferido", {
        cliente_origen_id,
        nombre_referido,
        telefono_referido,
      });
    },
    [executeTransaction]
  );

  const actualizarEstadoReferido = useCallback(
    ({ referido_id, estado, monto_compra }) => {
      return executeTransaction("actualizarEstadoReferido", {
        referido_id,
        estado,
        monto_compra,
      });
    },
    [executeTransaction]
  );

  const inicializarSeguridadUsuario = useCallback(
    async (input) => {
      const emailCandidate =
        typeof input === "string" ? input : input && typeof input === "object" ? input.email : "";
      const email = normalizeEmail(emailCandidate);
      if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
        return false;
      }

      const result = await executeTransaction("inicializarSeguridadUsuario", { email }, { noOfflineQueue: true });
      return !!result?.ok;
    },
    [executeTransaction]
  );

  const agregarCorreoPermitido = useCallback(
    (email) => executeTransaction("agregarCorreoPermitido", { email }),
    [executeTransaction]
  );

  const removerCorreoPermitido = useCallback(
    (email) => executeTransaction("removerCorreoPermitido", { email }),
    [executeTransaction]
  );

  const asignarRolCorreo = useCallback(
    ({ email, rol }) => executeTransaction("asignarRolCorreo", { email, rol }),
    [executeTransaction]
  );

  const registrarCampanaSocial = useCallback(
    async ({ canal, contenido, objetivo, codigo, descuento }) => {
      const result = await executeTransaction("registrarCampanaSocial", {
        canal,
        contenido,
        objetivo,
        codigo,
        descuento,
      });
      return result?.id || null;
    },
    [executeTransaction]
  );

  const actualizarEstadoCampana = useCallback(
    ({ campana_id, codigo, estado }) =>
      executeTransaction("actualizarEstadoCampana", { campana_id, codigo, estado }),
    [executeTransaction]
  );

  const vincularInteresCatalogo = useCallback(
    async ({ interes_id, cliente_id, vinculado_por }) => {
      const result = await executeTransaction("vincularInteresCatalogo", {
        interes_id,
        cliente_id,
        vinculado_por: vinculado_por || "",
      });
      return result;
    },
    [executeTransaction]
  );

  const desvincularInteresCatalogo = useCallback(
    async ({ interes_id }) => {
      const result = await executeTransaction("desvincularInteresCatalogo", { interes_id });
      return result;
    },
    [executeTransaction]
  );

  const campanasConRendimiento = useMemo(() => {
    const map = {};
    const codeToId = {};

    (state.campanasSociales || []).forEach((c) => {
      map[c.id] = {
        id: c.id,
        codigo: normalizeCampaignCode(c.codigo),
        canal: cleanText(c.canal || "INSTAGRAM"),
        objetivo: cleanText(c.objetivo || "TRAFICO_TIENDA"),
        contenido: cleanText(c.contenido),
        descuento: c.descuento || null,
        fecha: c.fecha || null,
        estado: cleanText(c.estado || "ACTIVA"),
        ventas: 0,
        ingresos: 0,
        margen: 0,
        descuentoVentas: 0,
        ticketPromedio: 0,
        ultimaVenta: null,
      };
      if (map[c.id].codigo) {
        codeToId[map[c.id].codigo] = c.id;
      }
    });

    (state.ventas || [])
      .filter((v) => !v.anulado)
      .forEach((v) => {
        const saleCode = normalizeCampaignCode(v.codigo_campana);
        const byId = cleanText(v.campana_id);
        const key = byId && map[byId] ? byId : codeToId[saleCode];

        let targetKey = key;
        if (!targetKey && saleCode) {
          targetKey = `MANUAL-${saleCode}`;
          if (!map[targetKey]) {
            map[targetKey] = {
              id: targetKey,
              codigo: saleCode,
              canal: "SIN_CANAL",
              objetivo: "SIN_OBJETIVO",
              contenido: "",
              descuento: null,
              fecha: null,
              estado: "NO_REGISTRADA",
              ventas: 0,
              ingresos: 0,
              margen: 0,
              descuentoVentas: 0,
              ticketPromedio: 0,
              ultimaVenta: null,
            };
          }
        }

        if (!targetKey || !map[targetKey]) return;

        const m = map[targetKey];
        m.ventas += 1;
        m.ingresos += parseFloat(v.total) || 0;
        m.margen += parseFloat(v.margen_bruto) || 0;
        m.descuentoVentas += parseFloat(v.descuento_total) || 0;

        if (!m.ultimaVenta || new Date(v.fecha) > new Date(m.ultimaVenta)) {
          m.ultimaVenta = v.fecha;
        }
      });

    return Object.values(map)
      .map((c) => ({
        ...c,
        ticketPromedio: c.ventas > 0 ? c.ingresos / c.ventas : 0,
      }))
      .sort((a, b) => b.ingresos - a.ingresos);
  }, [state.campanasSociales, state.ventas]);

  /* =========================
     REPORTES INTELIGENTES
  ========================== */

  const reportesInteligentes = useMemo(() => {
    const activeSales = state.ventas.filter((v) => !v.anulado);
    const activeSaleIds = new Set(activeSales.map((v) => v.id));
    const now = Date.now();

    const detalleActivos = state.detalleVentas.filter((d) => activeSaleIds.has(d.venta_id));

    const ventasMap = {};
    activeSales.forEach((v) => {
      ventasMap[v.id] = v;
    });

    const soldByProduct = {};
    const lastSaleByProduct = {};
    const soldBySize = {};
    const soldByColor = {};
    const costByProduct = buildAverageCostMap(state.productos, state.detalleCompras);

    detalleActivos.forEach((d) => {
      soldByProduct[d.producto_id] = (soldByProduct[d.producto_id] || 0) + (parseInt(d.cantidad) || 0);

      const venta = ventasMap[d.venta_id];
      if (venta) {
        const prev = lastSaleByProduct[d.producto_id];
        if (!prev || new Date(venta.fecha) > new Date(prev)) {
          lastSaleByProduct[d.producto_id] = venta.fecha;
        }
      }

      const sizeKey = `${cleanText(d.nombre || "SIN_BASE")}::${cleanText(d.talla || "UNICA")}`;
      soldBySize[sizeKey] = (soldBySize[sizeKey] || 0) + (parseInt(d.cantidad) || 0);

      const colorKey = cleanText(d.color || "SIN_COLOR");
      if (!soldByColor[colorKey]) soldByColor[colorKey] = { unidades: 0, ingreso: 0, costo: 0 };
      const qty = parseInt(d.cantidad) || 0;
      const unitCost = Math.max(
        0,
        parseFloat(d.costo_unitario) || parseFloat(costByProduct[d.producto_id]) || 0
      );
      soldByColor[colorKey].unidades += parseInt(d.cantidad) || 0;
      soldByColor[colorKey].ingreso += qty * (parseFloat(d.precio_unitario) || 0);
      soldByColor[colorKey].costo += qty * unitCost;
    });

    const inventarioLento = productosActivos
      .map((p) => {
        const lastSale = lastSaleByProduct[p.id];
        const diasSinVenta = lastSale
          ? Math.floor((now - new Date(lastSale).getTime()) / MS_DAY)
          : 999;
        return {
          producto_id: p.id,
          nombre: p.nombre,
          sku: p.sku,
          talla: p.talla,
          color: p.color,
          stock: p.stock,
          diasSinVenta,
          motivo: diasSinVenta >= 45 ? "Sin rotación" : "Rotación baja",
        };
      })
      .filter((x) => x.stock > 0 && x.diasSinVenta >= 30)
      .sort((a, b) => b.diasSinVenta - a.diasSinVenta)
      .slice(0, 20);

    const quiebreTallas = Object.entries(soldBySize)
      .map(([key, vendidos]) => {
        const [base, talla] = key.split("::");
        const stockTalla = productosActivos
          .filter((p) => cleanText(p.nombre) === base && cleanText(p.talla || "UNICA") === talla)
          .reduce((acc, p) => acc + p.stock, 0);
        return {
          producto_base: base,
          talla,
          vendidos,
          stock: stockTalla,
          riesgo: stockTalla <= state.config.stockMinimo ? "ALTO" : "MEDIO",
        };
      })
      .sort((a, b) => b.vendidos - a.vendidos)
      .slice(0, 20);

    const coloresRendimiento = Object.entries(soldByColor)
      .map(([color, data]) => ({
        color,
        unidades: data.unidades,
        ingreso: data.ingreso,
        margen: data.ingreso - data.costo,
      }))
      .sort((a, b) => b.unidades - a.unidades)
      .slice(0, 10);

    const margenVsVolumen = Object.entries(soldByProduct)
      .map(([productoId, unidades]) => {
        const product = productosActivos.find((p) => p.id === productoId);
        const detalles = detalleActivos.filter((d) => d.producto_id === productoId);
        const ingreso = detalles.reduce(
          (acc, d) => acc + (parseInt(d.cantidad) || 0) * (parseFloat(d.precio_unitario) || 0),
          0
        );
        const costo = detalles.reduce(
          (acc, d) =>
            acc +
            (parseInt(d.cantidad) || 0) *
              Math.max(
                0,
                parseFloat(d.costo_unitario) || parseFloat(costByProduct[d.producto_id]) || 0
              ),
          0
        );
        return {
          producto_id: productoId,
          nombre: product?.nombre || "Producto",
          unidades,
          ingreso,
          margen: ingreso - costo,
        };
      })
      .sort((a, b) => b.margen - a.margen)
      .slice(0, 15);

    const clientesTop = clientesConGrowth
      .map((c) => ({
        cliente_id: c.id,
        nombre: c.nombre,
        monto: c.stats.montoAcumulado,
        compras: c.stats.compras,
        ultimaCompra: c.stats.ultimaCompra,
        puntoSaldo: c.puntos,
      }))
      .sort((a, b) => b.monto - a.monto)
      .slice(0, 15);

    const compraPorHora = {};
    activeSales.forEach((v) => {
      const h = new Date(v.fecha).getHours();
      compraPorHora[h] = (compraPorHora[h] || 0) + 1;
    });
    const ventanasCompra = Object.entries(compraPorHora)
      .map(([hora, cantidad]) => ({ hora: parseInt(hora), cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 3);

    const alertas = [];
    reposicionSugerida.slice(0, 5).forEach((r) => {
      if (r.urgente) {
        alertas.push({
          tipo: "REPOSICION",
          mensaje: `Reponer ${r.nombre} ${r.talla || ""} (${r.color || ""}) en 48h`,
          prioridad: "ALTA",
        });
      }
    });

    inventarioLento.slice(0, 3).forEach((p) => {
      alertas.push({
        tipo: "INVENTARIO_LENTO",
        mensaje: `${p.nombre} lleva ${p.diasSinVenta} días sin venta. Activar promo de rotación`,
        prioridad: "MEDIA",
      });
    });

    clientesConGrowth
      .filter((c) => c.stats.riesgoChurn === "ALTO")
      .slice(0, 3)
      .forEach((c) => {
        alertas.push({
          tipo: "CLIENTE_INACTIVO",
          mensaje: `Reactivar a ${c.nombre} con campaña personalizada`,
          prioridad: "ALTA",
        });
      });

    return {
      inventarioLento,
      quiebreTallas,
      coloresRendimiento,
      margenVsVolumen,
      clientesTop,
      ventanasCompra,
      alertas,
    };
  }, [clientesConGrowth, productosActivos, reposicionSugerida, state.config.stockMinimo, state.detalleVentas, state.ventas]);

  const dismissFlushError = useCallback(() => {
    setFlushingStatus("idle");
    setFlushError(null);
  }, []);

  // Removes the stuck transaction from the queue so the next one can be attempted.
  // Use when the error is unrecoverable and the user wants to skip it deliberately.
  const discardFailedTransaction = useCallback(() => {
    const txId = flushError?.transactionId;
    if (txId) {
      retailPosOfflineQueue.dequeue(txId);
      setPendingCount(retailPosOfflineQueue.list().length);
    }
    setFlushingStatus("idle");
    setFlushError(null);
  }, [flushError?.transactionId]);

  return {
    state,
    syncStatus,
    pendingCount,
    flushPendingTransactions,
    flushingStatus,
    flushError,
    dismissFlushError,
    discardFailedTransaction,
    config: state.config,
    productos: productosActivos,
    ventas: state.ventas,
    detalleVentas: state.detalleVentas,
    compras: state.compras,
    detalleCompras: state.detalleCompras,
    movimientosInventario: state.movimientosInventario,
    clientes: state.clientes,
    interaccionesWhatsapp: state.interaccionesWhatsapp,
    cambiosDevoluciones: state.cambiosDevoluciones,
    creditosTienda: state.creditosTienda,
    promociones: state.promociones,
    promocionesActivas,
    fidelizacionLedger: state.fidelizacionLedger,
    referidos: state.referidos,
    campanasSociales: state.campanasSociales,
    interesesCatalogo: state.interesesCatalogo,
    securityPolicy: state.security,
    campanasConRendimiento,

    // Config
    actualizarConfig,

    // Productos
    agregarProducto,
    editarProducto,
    desactivarProducto,
    buscarProductoPorCodigo,
    buscarProductos,
    buscarProductosInactivos,
    obtenerCostoPromedioProducto,

    // Ventas
    registrarVenta,
    anularVenta,

    // Compras
    registrarCompra,

    // CRM
    upsertCliente,
    registrarInteraccionWhatsapp,
    enviarCampanaWhatsapp,

    // Cambios / devoluciones
    validarCambioDevolucion,
    registrarCambioDevolucion,

    // Promociones
    guardarPromocion,
    evaluarPromocionesCarrito,

    // Growth
    registrarCanjeFidelizacion,
    registrarReferido,
    actualizarEstadoReferido,
    registrarCampanaSocial,
    actualizarEstadoCampana,
    vincularInteresCatalogo,
    desvincularInteresCatalogo,
    refreshInteresesCatalogo,
    inicializarSeguridadUsuario,
    agregarCorreoPermitido,
    removerCorreoPermitido,
    asignarRolCorreo,

    // Reportes
    ventasActivas,
    ventasDelDia,
    ventasDelMes,
    totalVentasDelDia,
    totalVentasDelMes,
    productosMasVendidos,
    productosBajoStock,
    clientesSegmentados,
    clientesConGrowth,
    variantesPorBase,
    reposicionSugerida,
    puntosPorCliente,
    reportesInteligentes,
  };
}
